import React, { useState } from "react";
import Dropdown from "components/dropdown";
import { FiAlignJustify } from "react-icons/fi";
import { Link, useNavigate } from "react-router-dom";
import { MdPerson, MdLogout, MdLightMode, MdDarkMode } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { clearAdminProfile } from "store/slices/authSlice";
import { removeToken } from "utils/cookies.helpers";
import { adminLogout } from "api/adapters/auth/auth";
import { useTheme } from "contexts/ThemeContext";
import avatar from "assets/img/avatars/avatar4.png";

  const Navbar = (props) => {
    const { onOpenSidenav } = props;
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const adminProfile = useSelector((state) => state.auth.adminProfile);
    const { isDark, toggleTheme } = useTheme();
    const [loggingOut, setLoggingOut] = useState(false);

    const handleLogout = async () => {
      setLoggingOut(true);
      try {
        await adminLogout();
      } catch (_) {
        // ignore logout API errors, still clear session
      } finally {
        removeToken();
        dispatch(clearAdminProfile());
        navigate("/auth/sign-in");
        setLoggingOut(false);
      }
    };

  return (
    <nav className="sticky top-4 z-40 flex flex-row flex-wrap items-center justify-between rounded-xl bg-white px-4 py-3 shadow-lg shadow-orange-100/50 border border-orange-100/80 dark:!bg-navy-800 dark:shadow-none dark:border-white/10">
      {/* Left side */}
      <div className="flex items-center gap-3">
        {/* Hamburger for mobile */}
        <span
          className="flex cursor-pointer text-xl text-gray-600 dark:text-white xl:hidden"
          onClick={onOpenSidenav}
        >
          <FiAlignJustify className="h-5 w-5" />
        </span>

        {/* Branding pill — visible on desktop */}
        {/* <div className="hidden xl:flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-orange-500 to-orange-400 shadow-md shadow-orange-200 dark:shadow-none">
            <span className="text-sm font-black text-white">T</span>
          </div>
          <div>
            <p className="text-sm font-bold text-navy-700 dark:text-white leading-none">Tinka</p>
            <p className="text-[10px] text-orange-500 font-medium leading-none mt-0.5">Admin Panel</p>
          </div>
        </div> */}

        {/* Divider */}
        {/* <div className="hidden xl:block h-6 w-px bg-gray-200 dark:bg-white/10 mx-1" /> */}

        {/* Welcome message */}
        <div className="hidden sm:block">
          <p className="text-xl font-semibold text-navy-700 dark:text-white">
            Welcome back,{" "}
            <span className="text-orange-500">{adminProfile?.sUserName || "Admin"}</span>
          </p>
          <p className="text-sm text-gray-400 dark:text-gray-500">
            {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
          </p>
        </div>
      </div>

      {/* Right side */}
      <div className="flex items-center gap-2">
        {/* Theme toggle icon button */}
        <button
          onClick={toggleTheme}
          title={isDark ? "Switch to Light Mode" : "Switch to Dark Mode"}
          className="flex h-9 w-9 items-center justify-center rounded-full bg-orange-50 text-orange-500 hover:bg-orange-100 transition-colors dark:bg-[#1a1a1a] dark:text-orange-400 dark:hover:bg-[#222222]"
        >
          {isDark ? <MdLightMode className="h-5 w-5" /> : <MdDarkMode className="h-5 w-5" />}
        </button>

        {/* Divider */}
        <div className="h-6 w-px bg-gray-200 dark:bg-white/10 mx-1" />

        {/* Profile & Dropdown */}
        <Dropdown
          button={
            <div className="flex cursor-pointer items-center gap-2 rounded-full py-1 pl-1 pr-3 hover:bg-gray-50 transition-colors dark:hover:bg-navy-700">
              {adminProfile?.sProfilePicUrl ? (
                <img
                  className="h-9 w-9 rounded-full object-cover ring-2 ring-orange-200 dark:ring-orange-500/30"
                  src={adminProfile.sProfilePicUrl}
                  alt="Admin"
                />
              ) : (
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-orange-500 text-white ring-2 ring-orange-200 dark:ring-orange-500/30">
                  <MdPerson className="h-6 w-6" />
                </div>
              )}
              <div className="hidden sm:block text-left">
                <p className="text-xs font-bold text-navy-700 dark:text-white leading-none">
                  {adminProfile?.sUserName || "Admin"}
                </p>
                <p className="text-[10px] text-gray-400 leading-none mt-0.5">Super Admin</p>
              </div>
            </div>
          }
          children={
            <div className="flex w-56 flex-col justify-start rounded-[20px] bg-white shadow-xl shadow-shadow-500 dark:!bg-navy-700 dark:text-white dark:shadow-none overflow-hidden">
              <div className="bg-gradient-to-r from-orange-500 to-orange-400 px-4 py-4">
                <p className="text-sm font-bold text-white">
                  👋 Hey, {adminProfile?.sUserName || "Admin"}
                </p>
                {adminProfile?.sEmail && (
                  <p className="mt-0.5 text-xs text-orange-100">
                    {adminProfile.sEmail}
                  </p>
                )}
              </div>
              <div className="flex flex-col p-3 gap-0.5">
                <Link
                  to="/admin/profile"
                  className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-gray-700 hover:bg-orange-50 hover:text-orange-600 transition-colors dark:text-gray-300 dark:hover:bg-navy-600 dark:hover:text-white"
                >
                  <MdPerson className="h-4 w-4" />
                  Profile
                </Link>
                <div className="my-1 h-px w-full bg-gray-100 dark:bg-white/10" />
                <button
                  onClick={handleLogout}
                  disabled={loggingOut}
                  className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium text-red-500 hover:bg-red-50 transition-colors disabled:opacity-60"
                >
                  <MdLogout className="h-4 w-4" />
                  {loggingOut ? "Logging out..." : "Log Out"}
                </button>
              </div>
            </div>
          }
          classNames={"py-2 top-8 -left-[180px] w-max"}
        />
      </div>
    </nav>
  );
};

export default Navbar;

